import { apiGet } from '../services/api';
import type { Employee } from './employees';
import dayjs from 'dayjs';

export interface AttendanceRecord {
  id: number;
  user_id: number;
  date: string;
  check_in_at: string | null;
  check_out_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AttendanceResponse {
  attendance: AttendanceRecord;
  checked_in: boolean;
  checked_out: boolean;
}

export interface EmployeeAttendance {
  employee: Employee;
  attendance: AttendanceRecord | null;
  status: 'absent' | 'inOffice' | 'left';
}

export interface AttendanceQuery {
  /** Restrict the records to a single employee. */
  userId?: number;
  /** Restrict the records to a single branch (sent for Branch Managers). */
  branchId?: number;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Fetch all employees, optionally restricted to a single branch
 */
export async function getAllEmployees(branchId?: number): Promise<Employee[]> {
  const params = new URLSearchParams();
  params.append('per_page', '1000');

  if (branchId !== undefined) {
    params.append('branch_id', String(branchId));
  }

  const response = await apiGet<{ data: Employee[] } | Employee[]>(
    `/api/employees?${params.toString()}`,
  );
  const employees = Array.isArray(response) ? response : response.data;

  // The endpoint may ignore branch_id, so keep the branch scope on the client too.
  return branchId !== undefined
    ? employees.filter((employee) => employee.branch_id === branchId)
    : employees;
}

/**
 * Normalise the attendance endpoint response, which comes back either as a
 * plain array, a paginated payload, or a single attendance wrapper
 */
function extractAttendanceRecords(response: any): AttendanceRecord[] {
  if (Array.isArray(response)) {
    return response;
  }

  if (response && typeof response === 'object') {
    if (Array.isArray(response.data)) {
      return response.data;
    }

    if (Array.isArray(response.data?.data)) {
      return response.data.data;
    }

    if (response.attendance) {
      return [response.attendance];
    }
  }

  // Default to empty array if response format is unexpected
  console.warn('Unexpected attendance response format:', response);
  return [];
}

/**
 * Fetch attendance records for a date range, optionally for a single employee
 * and/or a single branch
 */
export async function getAttendanceRecords({
  userId,
  branchId,
  dateFrom,
  dateTo,
}: AttendanceQuery): Promise<AttendanceRecord[]> {
  const params = new URLSearchParams();

  if (dateFrom) {
    params.append('date_from', dateFrom);
  }
  if (dateTo) {
    params.append('date_to', dateTo);
  }
  if (userId !== undefined) {
    params.append('user_id', String(userId));
  }
  if (branchId !== undefined) {
    params.append('branch_id', String(branchId));
  }

  const response = await apiGet<{ data: AttendanceResponse[] } | AttendanceResponse[] | any>(
    `/api/face-auth/attendance/all?${params.toString()}`,
  );

  return extractAttendanceRecords(response);
}

/**
 * Fetch today's attendance records
 */
export async function getTodayAttendance(branchId?: number): Promise<AttendanceRecord[]> {
  const today = dayjs().format('YYYY-MM-DD');
  return getAttendanceRecords({ dateFrom: today, dateTo: today, branchId });
}

/**
 * Match employees with attendance records and determine status
 */
export function matchEmployeesWithAttendance(
  employees: Employee[],
  attendanceList: AttendanceRecord[]
): EmployeeAttendance[] {
  // Ensure attendanceList is an array
  const safeAttendanceList = Array.isArray(attendanceList) ? attendanceList : [];

  return employees.map((employee) => {
    const attendanceResponse = safeAttendanceList.find(
      (item) => item?.user_id === employee.id
    );
    const attendance = attendanceResponse || null;

    let status: 'absent' | 'inOffice' | 'left' = 'absent';

    if (attendance) {
      if (attendance.check_in_at && attendance.check_out_at) {
        status = 'left';
      } else if (attendance.check_in_at) {
        status = 'inOffice';
      }
    }

    return {
      employee,
      attendance,
      status,
    };
  });
}

/**
 * Fetch and match employees with today's attendance
 */
export async function getEmployeeAttendanceList(
  branchId?: number,
): Promise<EmployeeAttendance[]> {
  const [employees, attendanceList] = await Promise.all([
    getAllEmployees(branchId),
    getTodayAttendance(branchId),
  ]);

  return matchEmployeesWithAttendance(employees, attendanceList);
}

